import * as Icons from 'lucide-react'
import type { AccountSummary } from '../types'

interface SummaryCardsProps {
  summary: AccountSummary | null
  loading?: boolean
}

// Format number with K/M suffix
function formatCompact(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`
  }
  return value.toFixed(0)
}

interface MetricCardProps {
  title: string
  value: string | number
  prefix?: string
  suffix?: string
  icon?: React.ComponentType<{ className?: string }>
  loading?: boolean
}

function MetricCard({ title, value, prefix = '', suffix = '', icon: Icon, loading }: MetricCardProps) {
  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-5 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 bg-tf-bg-hover rounded w-24" />
          <div className="w-8 h-8 bg-tf-bg-hover rounded-lg" />
        </div>
        <div className="h-8 bg-tf-bg-hover rounded w-32" />
      </div>
    )
  }

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-tf-text-muted">{title}</p>
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-tf-bg-muted flex items-center justify-center">
            <Icon className="w-4 h-4 text-tf-text-muted" />
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-tf-text">
        {prefix}
        {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}
        {suffix}
      </p>
    </div>
  )
}

export function SummaryCards({ summary, loading }: SummaryCardsProps) {
  if (loading || !summary) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(8)].map((_, i) => (
          <MetricCard key={i} title="" value="" loading />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Primary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Spend"
          value={summary.total_spend}
          prefix="$"
          icon={Icons.DollarSign}
        />
        <MetricCard
          title="Total Revenue"
          value={summary.total_revenue}
          prefix="$"
          icon={Icons.TrendingUp}
        />
        <MetricCard
          title="ROAS"
          value={summary.average_roas.toFixed(2)}
          suffix="x"
          icon={Icons.Target}
        />
        <MetricCard
          title="CPA"
          value={summary.average_cpa}
          prefix="$"
          icon={Icons.CircleDollarSign}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Impressions"
          value={formatCompact(summary.total_impressions)}
          icon={Icons.Eye}
        />
        <MetricCard
          title="Clicks"
          value={formatCompact(summary.total_clicks)}
          icon={Icons.MousePointerClick}
        />
        <MetricCard
          title="Conversions"
          value={formatCompact(summary.total_conversions)}
          icon={Icons.ShoppingCart}
        />
        <MetricCard
          title="Active Campaigns"
          value={`${summary.active_campaigns}/${summary.campaign_count}`}
          icon={Icons.Layers}
        />
      </div>
    </div>
  )
}

export default SummaryCards
